import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [isVisible, setIsVisible] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (footerRef.current) observer.observe(footerRef.current);
    return () => {
      if (footerRef.current) observer.unobserve(footerRef.current);
    };
  }, []);

  useEffect(() => {
    if (showTermsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTermsModal]);

  const footerLinks = {
    'Quick Links': [
      { name: 'Inventory', path: '/inventory' },
      { name: 'Financing', path: '/financing' },
      { name: 'About Us', path: '/about' },
      { name: 'Contact', path: '/contact' },
    ],
    Services: [
      { name: 'New Vehicles', path: '/inventory' },
      { name: 'Pre-Owned', path: '/inventory' },
      { name: 'Trade-In', path: '/financing' },
      { name: 'Apply for Financing', path: '/financing' },
    ],
    Resources: [
      { name: 'Financing Calculator', path: '/financing' },
      { name: 'FAQs', path: '/contact' },
      { name: 'Nationwide Shipping', path: '/contact' },
    ],
  };

  return (
    <>
      <footer ref={footerRef} className="bg-[#080808] border-t border-white/5">
        {/* Top gold line */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent"></div>

        <div className="max-w-7xl mx-auto px-8 lg:px-16 py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Brand */}
            <div className={`lg:col-span-2 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png"
                  alt="Paramount Motors Logo"
                  style={{ height: '5rem', width: 'auto' }}
                  className="object-contain"
                />
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-3" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                Your trusted source for premium vehicles. We offer a curated selection of new, pre-owned, and certified vehicles from the world's most prestigious brands, with seamless nationwide delivery.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-2-fill text-[#C9A84C] text-sm"></i>
                </div>
                <span className="text-white/60 text-xs tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Based in <strong className="text-white/80 font-semibold">Miami, Florida</strong> &mdash; Serving the entire USA
                </span>
              </div>
              <div className="flex items-start gap-2 mb-6 p-3 bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-sm">
                <i className="ri-truck-line text-[#C9A84C] text-base mt-0.5"></i>
                <div>
                  <p className="text-white text-xs font-semibold mb-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Nationwide Shipping Available
                  </p>
                  <p className="text-white/70 text-xs" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                    We ship to all 48 contiguous states with insured transport
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                {[
                  { icon: 'ri-facebook-fill', href: '#' },
                  { icon: 'ri-instagram-line', href: '#' },
                  { icon: 'ri-twitter-x-line', href: '#' },
                  { icon: 'ri-linkedin-fill', href: '#' },
                ].map((social, index) => (
                  <a
                    key={social.icon}
                    href={social.href}
                    className={`w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#C9A84C] hover:text-[#C9A84C] text-white/60 rounded-sm transition-all cursor-pointer hover:scale-110 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ transitionDelay: `${300 + index * 80}ms` }}
                  >
                    <i className={`${social.icon} text-sm`}></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Links */}
            {Object.entries(footerLinks).map(([title, links], colIndex) => (
              <div
                key={title}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${400 + colIndex * 150}ms` }}
              >
                <h4 className="text-xs font-bold tracking-widest uppercase text-white mb-5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {title}
                </h4>
                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.path}
                        className="text-xs text-white/70 hover:text-[#C9A84C] transition-all cursor-pointer hover:translate-x-1 inline-block transform"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contact strip - HIDDEN */}
          {/* <div className={`grid grid-cols-1 sm:grid-cols-3 gap-6 py-8 border-t border-b border-white/5 mb-10 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            ...
          </div> */}

          {/* Disclosure */}
          <div className={`mb-10 p-4 bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-sm transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <p className="text-white/80 text-xs leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
              Paramount Motors is a premier vehicle brokerage. We source inventory via our trusted dealer network and do not maintain a physical lot. All vehicles are subject to prior sale.
            </p>
          </div>

          {/* Bottom bar */}
          <div className={`flex flex-col md:flex-row justify-between items-center gap-4 transition-all duration-1000 delay-900 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <p className="text-xs text-white/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              © 2025 Paramount Motors. All rights reserved.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowTermsModal(true)}
                className="text-xs text-white/70 hover:text-[#C9A84C] transition-colors cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Terms of Use
              </button>
              <span className="text-white/20">|</span>
              <button
                className="text-xs text-white/70 hover:text-[#C9A84C] transition-colors cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Terms of Use Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-[#C9A84C]/20 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Terms of Use
              </h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-[#C9A84C] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-6 space-y-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <p className="text-white/70 text-sm">
                <strong className="text-white/90">Effective Date:</strong> March 4, 2026
              </p>
              
              <p className="text-white/80 text-sm leading-relaxed">
                Welcome to Paramount Motors (the "Website"). By accessing or using our services, you agree to be bound by the following terms and conditions. Please read them carefully.
              </p>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  1. Paramount Motors Service Disclosure
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-2">
                  Paramount Motors is a professional vehicle brokerage firm.
                </p>
                <ul className="list-disc list-inside space-y-2 text-white/80 text-sm ml-4">
                  <li><strong className="text-white/90">No Owned Inventory:</strong> You acknowledge and agree that Paramount Motors does not own, title, or maintain a physical inventory of the vehicles displayed on this Website.</li>
                  <li><strong className="text-white/90">Brokerage Role:</strong> We act as an intermediary, sourcing premium vehicles from a curated network of licensed franchise dealers, private collections, and wholesalers.</li>
                  <li><strong className="text-white/90">Transaction Parties:</strong> Any purchase agreement for a vehicle is ultimately between you (the Buyer) and the third-party Seller. Paramount Motors facilitates the transaction but is not the selling entity of record.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  2. Accuracy of Listings & "Subject to Prior Sale"
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-2">
                  While we strive for excellence in our digital showroom, the data provided (including VINs, mileage, options, and pricing) is often supplied by third-party sources.
                </p>
                <ul className="list-disc list-inside space-y-2 text-white/80 text-sm ml-4">
                  <li><strong className="text-white/90">No Warranty on Descriptions:</strong> Paramount Motors does not warrant that vehicle descriptions are 100% accurate or error-free.</li>
                  <li><strong className="text-white/90">Real-Time Availability:</strong> All vehicles are "Subject to Prior Sale." Because we source from a global network, a vehicle may become unavailable before this Website is updated.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  3. Pricing and Fees
                </h3>
                <ul className="list-disc list-inside space-y-2 text-white/80 text-sm ml-4">
                  <li><strong className="text-white/90">Advertised Price:</strong> Prices listed generally do not include taxes, registration, title fees, or brokerage service fees unless explicitly stated in a signed Brokerage Agreement.</li>
                  <li><strong className="text-white/90">Errors:</strong> In the event a vehicle is listed at an incorrect price due to a typographical or technical error, Paramount Motors reserves the right to refuse or cancel any inquiries or expressions of interest.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  4. Vehicle Condition & Inspections
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-2">
                  Paramount Motors prides itself on quality, but we are not a mechanical repair facility.
                </p>
                <ul className="list-disc list-inside space-y-2 text-white/80 text-sm ml-4">
                  <li><strong className="text-white/90">Pre-Purchase Inspections (PPI):</strong> We strongly encourage all clients to commission an independent third-party inspection.</li>
                  <li><strong className="text-white/90">"As-Is" Status:</strong> Unless a manufacturer's warranty remains in effect, all vehicles are sourced "As-Is" from the seller. Paramount Motors provides no independent mechanical or structural warranties.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  5. Limitation of Liability
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-2">
                  To the fullest extent permitted by law, Paramount Motors and its affiliates shall not be liable for any direct, indirect, or consequential damages resulting from:
                </p>
                <ul className="list-disc list-inside space-y-2 text-white/80 text-sm ml-4">
                  <li>Mechanical failure or undisclosed history of a sourced vehicle.</li>
                  <li>Delays in transport or delivery handled by third-party logistics.</li>
                  <li>Any financial losses incurred through the use of this Website or our brokerage services.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  6. Intellectual Property
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  The "Paramount Motors" name, logo, and custom website content are the exclusive property of Paramount Motors. Unauthorized use, "scraping," or reproduction of our listings for commercial purposes is strictly prohibited.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  7. Governing Law
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  These Terms of Use shall be governed by and construed in accordance with the laws of the State of [Your State]. Any legal action arising from these terms shall be filed in the courts of [Your County].
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  8. Contact Us
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-2">
                  If you have any questions regarding these Terms or our brokerage process, please contact us through our Contact page.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/10">
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-full py-3 bg-[#C9A84C] hover:bg-[#B8973B] text-black font-semibold text-sm rounded-sm transition-colors cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}